/**
 * SendQuoteModal.jsx — Pre-flight confirmation for the Send Quote flow.
 *
 * Why a modal: sending a quote is high-stakes (the wrong PDF or wrong email
 * goes to a paying client). One click → 3 simultaneous actions is too easy
 * to fire by mistake. The modal lets the user verify To / CC / Subject /
 * body once and intentionally confirm.
 *
 * Three call sites (JobPipeline Step 2 / QuoteBrowser detail /
 * QuoteBuilder Save & Send) all open this modal with the same `quote` prop.
 * On confirm, runs `executeQuoteSend` which downloads the PDF, opens mailto,
 * and stamps `quotes.sent_at` / `status: sent`.
 *
 * Resend detection: if `quote.sentAt` exists, an informational "already sent"
 * banner shows and `sent_at` is preserved — but the email itself is composed
 * the same clean way every time (no "(REMINDER)" prefill).
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../../hooks/useSettings';
import { useUpdateQuote } from '../../hooks/useQuotes';
import { useCreateJob } from '../../hooks/useJobs';
import { useCreateInvoice, useInvoices } from '../../hooks/useInvoices';
import { useXeroStatus, useSyncInvoiceToXero } from '../../hooks/useXero';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../layout/Toast';
import { prepareQuoteSend, executeQuoteSend, buildJobFromQuote, DEPOSIT_LINK_PLACEHOLDER } from '../../lib/quoteSend';
import { fmtD, fmtC, uid, nextInvoiceNumber, toLocalDateStr, snakeToCamel } from '../../lib/utils';
import { queryKeys } from '../../lib/queryClient';
import { api } from '../../lib/api';

const inputCls =
  'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent';

function daysAgo(iso) {
  if (!iso) return 0;
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

export default function SendQuoteModal({
  quote,
  job = null,
  onClose,
  onSent,           // optional: (result) => void — called after successful send
}) {
  const { data: settings } = useSettings();
  const updateQuoteMut = useUpdateQuote();
  const createJobMut = useCreateJob();
  const createInvoiceMut = useCreateInvoice();
  const { data: allInvoices = [] } = useInvoices();
  const { data: xeroStatus } = useXeroStatus();
  const syncInvoiceMut = useSyncInvoiceToXero();
  const { profile } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const senderName = profile?.full_name || '';
  const xeroConnected = xeroStatus?.connected === true;

  // Deposit mode: 'none' | 'create' (make a new invoice, reusing one if it
  // already exists) | 'existing' (only fetch an already-created invoice's
  // payment link — never create).
  const [depositMode, setDepositMode] = useState('none');
  const depositPct = parseFloat(settings?.data?.depositPct ?? 50) || 50;
  const defaultDepositAmount = Math.round((parseFloat(quote?.total) || 0) * (depositPct / 100) * 100) / 100;
  const [depositAmount, setDepositAmount] = useState(defaultDepositAmount);

  // Existing non-void deposit invoice for this quote's job (if any).
  const existingDeposit = useMemo(() => {
    const jid = job?.id || quote?.jobId;
    if (!jid) return null;
    return allInvoices.find(
      (inv) => inv.jobId === jid && inv.invoiceType === 'deposit' && inv.status !== 'void'
    ) || null;
  }, [allInvoices, job?.id, quote?.jobId]);

  // A deposit is only "resolvable" when it can point to a real invoice: 'create'
  // mode (we make/reuse one) or 'existing' mode WITH an existing invoice. This
  // prevents fabricating a deposit/balance split tied to no invoice.
  const withDeposit = depositMode === 'create' || (depositMode === 'existing' && !!existingDeposit);
  // Amount shown in the email/PDF: an existing invoice is the source of truth;
  // only a freshly-created one uses the editable field.
  const effectiveDepositAmount = existingDeposit
    ? (parseFloat(existingDeposit.amount) || 0)
    : (parseFloat(depositAmount) || 0);
  // Intended deposit percentage for the PDF label — passed through so the PDF
  // doesn't reverse-derive it from its own recomputed total.
  const effectiveDepositPct = existingDeposit
    ? (parseFloat(quote?.total) > 0 ? (parseFloat(existingDeposit.amount) / parseFloat(quote.total)) * 100 : 0)
    : depositPct;

  // Build defaults from the quote. Recompute when quote/settings change so a
  // freshly-saved quote's most-recent state is reflected. The deposit mode/amount
  // feed in so the email body reflects the deposit request (with a placeholder
  // link). The prep object also carries _buildPdf, invoked by executeQuoteSend.
  const prep = useMemo(
    () => (quote ? prepareQuoteSend({ quote, job, settings, senderName, withDeposit, depositAmount: effectiveDepositAmount, depositPct: effectiveDepositPct }) : null),
    [quote, job, settings, senderName, withDeposit, effectiveDepositAmount, effectiveDepositPct]
  );

  const [toEmail, setToEmail] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [doDownload, setDoDownload] = useState(true);
  const [doMailto, setDoMailto] = useState(true);
  const [doStampSent, setDoStampSent] = useState(true);
  const [bodyOpen, setBodyOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // Track manual edits so re-seeding (on a deposit toggle / amount change) never
  // clobbers what the operator typed into Subject or Body.
  const subjectDirty = useRef(false);
  const bodyDirty = useRef(false);

  // Seed non-body fields when the modal opens for a (different) quote. Keyed on
  // quote id so toggling the deposit option doesn't wipe To/CC/Subject edits.
  useEffect(() => {
    if (!quote) return;
    setToEmail(quote.clientEmail || '');
    setCc('');
    setDoDownload(true);
    setDoMailto(true);
    setDoStampSent(true);
    setDepositMode('none');
    setDepositAmount(defaultDepositAmount);
    setSending(false);
    subjectDirty.current = false;
    bodyDirty.current = false;
  }, [quote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subject + body follow the prep — but only until the operator edits them.
  // Once a field is dirty we leave it alone so a deposit toggle / amount change
  // can't silently revert personalizations. The deposit pay link still reaches
  // the email via the placeholder swap (or append) in executeQuoteSend.
  useEffect(() => {
    if (!prep) return;
    if (!subjectDirty.current) setSubject(prep.defaultSubject);
    if (!bodyDirty.current) setBody(prep.defaultBody);
  }, [prep?.defaultSubject, prep?.defaultBody]);

  if (!quote || !prep) return null;

  const isResend = prep.isResend;

  // Ensure a job exists so the opportunity reaches the board and the deposit
  // invoice can link to it. No-op when one is already linked.
  const ensureJobAsync = async () => {
    if (job?.id) return job.id;
    if (quote.jobId) return quote.jobId;
    const newJob = await createJobMut.mutateAsync(buildJobFromQuote(quote));
    if (!newJob?.id) throw new Error('Job creation returned no id');
    // Link the new job to the quote — but don't discard the freshly-created job
    // id if the link write fails, or the next send would mint a duplicate job.
    try {
      await updateQuoteMut.mutateAsync({ id: quote.id, jobId: newJob.id });
    } catch (e) {
      toast(`Job created but couldn't link it to the quote — please re-link to avoid a duplicate (${e.message || 'unknown error'})`, 'error');
    }
    return newJob.id;
  };

  // Resolve the deposit payment link for the email. In 'create' mode we make a
  // new invoice when none exists (reusing one if it does, to avoid duplicates);
  // in 'existing' mode we only fetch an already-created invoice's link and never
  // create. Returns { link, missing } — missing=true when 'existing' finds none.
  const resolveDepositAsync = async (jobId) => {
    // Dedupe + number against SERVER-FRESH invoices, not the render-time cache —
    // otherwise a deposit invoice made elsewhere (Job Pipeline, another tab, an
    // in-flight create) is missed and we'd mint a duplicate (and number).
    let fresh = allInvoices;
    try {
      fresh = await qc.fetchQuery({
        queryKey: queryKeys.invoices.all,
        queryFn: async () => (await api.get('/invoices')).map(snakeToCamel),
        staleTime: 0,
      });
    } catch { /* network blip — fall back to the cached list */ }
    let invoice = fresh.find(
      (inv) => inv.jobId === jobId && inv.invoiceType === 'deposit' && inv.status !== 'void'
    );
    if (!invoice && depositMode === 'create') {
      invoice = {
        id: uid(),
        number: nextInvoiceNumber(settings?.data, fresh),
        jobId,
        client: quote.clientName || '',
        invoiceType: 'deposit',
        amount: parseFloat(depositAmount) || 0,
        paidAmount: 0,
        status: 'unpaid',
        terms: settings?.data?.payTerms || 'Net 30',
        dueDate: toLocalDateStr(new Date()),
        notes: '',
        payments: [],
      };
      await createInvoiceMut.mutateAsync(invoice);
    }
    if (!invoice) return { link: '', missing: true };
    let link = invoice.xeroOnlineUrl || '';
    if (xeroConnected && !link) {
      try {
        const r = await syncInvoiceMut.mutateAsync(invoice.id);
        link = r?.xeroOnlineUrl || '';
      } catch { /* invoice exists; its link can be synced later — don't abort the send */ }
    }
    return { link, invoiceNumber: invoice.number };
  };

  const handleConfirm = async () => {
    if (sending) return;
    if (doMailto && !toEmail.trim()) {
      toast('Recipient email is required', 'error');
      return;
    }
    if (!doDownload && !doMailto && !doStampSent) {
      toast('Pick at least one action', 'error');
      return;
    }
    if (depositMode === 'existing' && !existingDeposit) {
      toast('No existing deposit invoice for this job — switch to "Create", or make one in the Job Pipeline first.', 'error');
      return;
    }

    setSending(true);
    try {
      const result = await executeQuoteSend({
        quote,
        prep,
        toEmail: toEmail.trim(),
        cc: cc.trim(),
        subject,
        body,
        doDownload,
        doMailto,
        doStampSent,
        updateQuoteAsync: updateQuoteMut.mutateAsync,
        ensureJobAsync,
        deposit: withDeposit ? { amount: effectiveDepositAmount, placeholder: DEPOSIT_LINK_PLACEHOLDER } : null,
        createDepositAsync: resolveDepositAsync,
        settings,
        onWarn: (msg) => toast(msg, 'error'),
      });

      // Toast a single line summarising what happened
      const parts = [];
      if (result.pdfDownloaded) parts.push('PDF downloaded');
      if (result.mailtoOpened) parts.push('email draft opened');
      if (result.depositIncluded) parts.push('deposit link included');
      if (result.statusUpdated) parts.push(isResend ? 'marked resent' : 'marked sent');
      toast(parts.length ? `Quote sent — ${parts.join(', ')}.` : 'Done', 'success');

      onSent?.(result);
      onClose?.();
    } catch (err) {
      console.error('SendQuoteModal failed:', err);
      toast(`Failed to send: ${err.message || 'unknown error'}`, 'error');
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[1300] flex items-center justify-center p-4 pb-[calc(56px+env(safe-area-inset-bottom,0px))] md:pb-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !sending) onClose?.();
      }}
    >
      <div
        className="bg-surface border border-border rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-quote-title"
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
          <div>
            <h2 id="send-quote-title" className="text-sm font-heading text-text-primary uppercase tracking-wide">
              {isResend ? 'Resend Quote' : 'Send Quote'}
            </h2>
            <p className="text-2xs text-text-dim mt-0.5">
              {quote.quoteNumber || 'Untitled'} · {quote.clientName || 'No client'}
            </p>
          </div>
          <button
            onClick={() => !sending && onClose?.()}
            className="text-text-dim hover:text-text-primary text-xl leading-none px-2"
            aria-label="Close"
            disabled={sending}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto space-y-4 flex-1">
          {isResend && (
            <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-2xs text-amber-300 leading-relaxed">
              <strong>Already sent.</strong> This quote was first sent {daysAgo(quote.sentAt)} day
              {daysAgo(quote.sentAt) === 1 ? '' : 's'} ago ({fmtD(quote.sentAt)}). Sending again composes
              the same clean email — the original send date is preserved.
            </div>
          )}

          {/* What will happen — checkbox list */}
          <div className="space-y-2">
            <label className="text-2xs font-semibold text-text-muted uppercase tracking-wider block">
              What happens when you confirm
            </label>
            <label className="flex items-center gap-2 cursor-pointer hover:bg-surface-2 p-1.5 rounded transition-colors">
              <input
                type="checkbox"
                checked={doDownload}
                onChange={(e) => setDoDownload(e.target.checked)}
                className="accent-accent"
                disabled={sending}
              />
              <span className="text-xs text-text-primary">
                Download PDF
                <span className="text-text-dim ml-2 font-mono text-2xs">{prep.pdfFilename}</span>
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer hover:bg-surface-2 p-1.5 rounded transition-colors">
              <input
                type="checkbox"
                checked={doMailto}
                onChange={(e) => { setDoMailto(e.target.checked); if (!e.target.checked) setDepositMode('none'); }}
                className="accent-accent"
                disabled={sending}
              />
              <span className="text-xs text-text-primary">Open email draft in your mail app</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer hover:bg-surface-2 p-1.5 rounded transition-colors">
              <input
                type="checkbox"
                checked={doStampSent}
                onChange={(e) => setDoStampSent(e.target.checked)}
                className="accent-accent"
                disabled={sending}
              />
              <span className="text-xs text-text-primary">
                {isResend
                  ? 'Stamp a fresh resend timestamp'
                  : 'Mark quote as sent + transition status'}
              </span>
            </label>
          </div>

          {/* Deposit options — only when Xero is connected (the link comes from Xero) */}
          {xeroConnected && (
            <div className="space-y-2 rounded border border-accent/40 bg-accent/5 p-2.5">
              <span className="text-2xs font-semibold text-text-muted uppercase tracking-wider block">
                Deposit (Xero)
              </span>

              {/* Option 1 — create (or reuse) a deposit invoice */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={depositMode === 'create'}
                  onChange={(e) => setDepositMode(e.target.checked ? 'create' : 'none')}
                  className="accent-accent"
                  disabled={sending || !doMailto}
                />
                <span className="text-xs text-text-primary font-medium">
                  Create deposit invoice &amp; include payment link
                </span>
              </label>
              {depositMode === 'create' && (
                <div className="pl-6 space-y-1.5">
                  {existingDeposit ? (
                    <p className="text-2xs text-text-dim leading-snug">
                      An existing deposit invoice ({existingDeposit.number} · {fmtC(existingDeposit.amount)}) will be
                      reused — no duplicate is created.
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-2xs font-medium text-text-muted uppercase tracking-wider">Amount</span>
                      <input
                        type="number"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        step="0.01"
                        min="0"
                        className="w-32 px-2 py-1 bg-surface border border-border rounded text-text-primary text-xs focus:outline-none focus:border-accent"
                        disabled={sending}
                      />
                      <span className="text-2xs text-text-dim">{depositPct}% of {fmtC(quote.total)}</span>
                    </div>
                  )}
                  <p className="text-2xs text-text-dim leading-snug">
                    Created/synced in Xero when you send — the payment link replaces the placeholder in the email body.
                  </p>
                </div>
              )}

              {/* Option 2 — fetch only an already-created invoice's link */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={depositMode === 'existing'}
                  onChange={(e) => setDepositMode(e.target.checked ? 'existing' : 'none')}
                  className="accent-accent"
                  disabled={sending || !doMailto}
                />
                <span className="text-xs text-text-primary font-medium">
                  Use existing deposit invoice &amp; payment link
                </span>
              </label>
              {depositMode === 'existing' && (
                <div className="pl-6 space-y-1.5">
                  {existingDeposit ? (
                    <p className="text-2xs text-text-dim leading-snug">
                      Found {existingDeposit.number} · {fmtC(existingDeposit.amount)} ({existingDeposit.status}).
                      Its payment link is included — nothing new is created.
                    </p>
                  ) : (
                    <p className="text-2xs text-amber-300 leading-snug">
                      No deposit invoice found for this job yet — nothing will be created. Pick “Create” instead,
                      or make one in the Job Pipeline first.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="rounded border border-border bg-surface-2/40 p-2 text-2xs text-text-dim leading-snug">
            <strong className="text-text-muted">Heads up:</strong> mailto: links can't auto-attach.
            After your mail app opens, drag the downloaded PDF into the email body before sending.
          </div>

          {/* To / CC / Subject */}
          <div className="space-y-3">
            <div>
              <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                To
              </label>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className={inputCls}
                disabled={sending || !doMailto}
                placeholder="client@example.com"
              />
            </div>
            <div>
              <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                CC <span className="text-text-dim font-normal normal-case">(optional, comma-separated)</span>
              </label>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                className={inputCls}
                disabled={sending || !doMailto}
                placeholder="agent@example.com, assistant@example.com"
              />
            </div>
            <div>
              <label className="block text-2xs font-medium text-text-muted uppercase tracking-wider mb-1">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => { setSubject(e.target.value); subjectDirty.current = true; }}
                className={inputCls}
                disabled={sending || !doMailto}
              />
            </div>
          </div>

          {/* Body preview/edit (collapsible) */}
          <div>
            <button
              type="button"
              onClick={() => setBodyOpen((v) => !v)}
              className="text-2xs text-text-dim hover:text-text-primary flex items-center gap-1 transition-colors"
            >
              <span>{bodyOpen ? '▼' : '▶'}</span>
              <span>{bodyOpen ? 'Hide email body' : 'Preview email body'}</span>
            </button>
            {bodyOpen && (
              <textarea
                value={body}
                onChange={(e) => { setBody(e.target.value); bodyDirty.current = true; }}
                rows={12}
                className={`${inputCls} mt-2 font-mono text-xs leading-relaxed resize-y`}
                disabled={sending || !doMailto}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 flex-shrink-0 bg-surface-2/30">
          <button
            onClick={() => !sending && onClose?.()}
            className="btn-ghost px-4 py-2 text-sm"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={() => handleConfirm()}
            disabled={sending || (doMailto && !toEmail.trim())}
            className="btn-primary px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending
              ? 'Sending…'
              : withDeposit
                ? (isResend ? 'Deposit & Resend' : 'Deposit & Send')
                : isResend
                  ? 'Download & Resend'
                  : 'Download & Open Email'}
          </button>
        </div>
      </div>
    </div>
  );
}
